import axios from 'axios';
import { useEffect, useState } from 'react';
import { LOOP54_DEFAULT_PAGE_SIZE } from '../components/Pagination/PaginationLoop54';
import { generateUUID } from '../components/utils';
import { useConfig } from '../config';
import { useDebounce } from '../hooks/useDebounce';
import { transformLoopResponseToApiProducts } from '../state/api';
import { MetaContainer } from '../state/meta';
import { ApiProduct, Loop54SearchRequestBody } from '../types';

const PAGE_SIZE = 32;
const INITIAL_RESULTS = {
	items: [],
	total: 0,
	searchedQuery: '',
	showResults: false,
};

type AddProductSearchReturn = {
	results: ProductSearchResults;
	query: string;
	setQuery: React.Dispatch<React.SetStateAction<string>>;
	loading: boolean;
	setPage: React.Dispatch<React.SetStateAction<number>>;
	areMoreResults: boolean;
};

export type ProductSearchResults = {
	items: ApiProduct[];
	total: number;
	searchedQuery: string;
	showResults: boolean;
};

export const useAddProductSearch = (): AddProductSearchReturn => {
	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<ProductSearchResults>(INITIAL_RESULTS);
	const [page, setPage] = useState(0);
	const [areMoreResults, setAreMoreResults] = useState(false);
	const debouncedQuery: string = useDebounce<string>(query, 500);
	const { loop54Api } = useConfig();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();

	useEffect(() => {
		if (query === '') {
			setLoading(false);
			setAreMoreResults(false);
			setResults(INITIAL_RESULTS);
		}
	}, [query]);

	useEffect(() => {
		setPage(0);
		setResults(INITIAL_RESULTS);
		setAreMoreResults(false);
	}, [debouncedQuery]);

	useEffect(() => {
		const controller = new AbortController();
		const currentQuery = debouncedQuery;
		const currentPage = page;
		const doSearch = async () => {
			try {
				setLoading(true);
				let loop54SearchPayload: Loop54SearchRequestBody = {
					query: debouncedQuery,
					resultsOptions: {
						take: LOOP54_DEFAULT_PAGE_SIZE,
						skip: page > 0 ? LOOP54_DEFAULT_PAGE_SIZE * page : page,
					},
					relatedResultsOptions: {
						take: 0,
						skip: 0,
					},
				};
				const loop54PostHeaders = {
					'Api-Version': 'V3',
					'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
				};
				let { data, status } = await axios.post(
					loop54Api + 'search',
					{ ...loop54SearchPayload },
					{ withCredentials: false, signal: controller.signal, headers: { ...loop54PostHeaders } }
				);

				// Ignore stale responses from older query/page requests.
				if (currentQuery !== debouncedQuery || currentPage !== page) return;

				if (status === 200) {
					const { results } = data;
					const total = results.count;
					setAreMoreResults((page + 1) * PAGE_SIZE <= total);
					let transformedProducts: ApiProduct[];

					if (data && data.results) {
						transformedProducts = transformLoopResponseToApiProducts(data.results.items);
						data.results.items = transformedProducts;
					}

					setResults((prev) => {
						return {
							items: [...prev.items, ...results.items.map((item: ApiProduct) => item)],
							total,
							searchedQuery: debouncedQuery,
							showResults: true,
						};
					});
				} else {
					console.error(`HTTP error! Status: ${data.status}`);
				}
			} catch (error) {
				if (!axios.isCancel(error)) {
					console.error(error);
				}
			} finally {
				setLoading(false);
			}
		};

		if (debouncedQuery) {
			doSearch();
		}

		return () => controller.abort();
	}, [debouncedQuery, page]);

	return { results, query, setQuery, loading, setPage, areMoreResults };
};
